import { getSession } from '../lib/auth';
import { fetchAudioContent } from '../lib/api';
import { audioCache } from './audioCache';
import { debugLog } from '../lib/debugLog';

class AudioManager {
  private context: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private customSounds: {
    focus?: { path: string; url?: string };
    shortBreak?: { path: string; url?: string };
    longBreak?: { path: string; url?: string };
  } = {};
  private audioBufferCache: Map<string, AudioBuffer> = new Map();
  private previewSource: AudioBufferSourceNode | null = null;
  private previewAudio: HTMLAudioElement | null = null;
  private audioElements: Map<string, HTMLAudioElement> = new Map();
  private isInitialized = false;
  public hasUserInteraction = false;
  private isPlaying = false;
  private currentSourceNode: AudioBufferSourceNode | null = null;
  private currentOscillator: OscillatorNode | null = null;
  private currentChimeOscillators: OscillatorNode[] = [];
  public soundStyle: 'smooth' | 'legacy' = 'smooth';

  async ensureInitialized(): Promise<boolean> {
    if (this.isInitialized && this.context?.state === 'running') return true;
  
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
  
      // If context exists but is closed, recreate it
      if (this.context?.state === 'closed') {
        this.context = null;
        this.gainNode = null;
      }
  
      this.context ??= new AudioContextClass();
      this.gainNode ??= this.context.createGain();
      this.gainNode.gain.value = 1;
      this.gainNode.connect(this.context.destination);
  
      if (this.context.state === 'suspended') {
        await this.context.resume();
        debugLog('[Audio] Resumed suspended AudioContext');
      }
  
      // Play silent buffer only once for unlock
      if (!this.isInitialized) {
        const buffer = this.context.createBuffer(1, 1, 22050);
        const source = this.context.createBufferSource();
        source.buffer = buffer;
        source.connect(this.context.destination);
        source.start(0);
      }
  
      this.isInitialized = true;
      this.hasUserInteraction = true;
      debugLog('[Audio] Initialized:', this.context.state);
      return true;
    } catch (error) {
      console.error('[Audio] Init failed:', error);
      return false;
    }
  }
  

  private async fetchRemoteAudio(path: string): Promise<ArrayBuffer> {
    const session = getSession();
    if (!session) throw new Error('Not authenticated');
    return fetchAudioContent(path);
  }

  private async fetchAndDecodeAudio(path: string): Promise<AudioBuffer> {
    if (this.audioBufferCache.has(path)) return this.audioBufferCache.get(path)!;
    
    // Try to get from cache first
    const cachedUrl = await audioCache.loadFromCache(path);
    if (cachedUrl) {
      const response = await fetch(cachedUrl);
      if (!response.ok) throw new Error(`HTTP error! ${response.status}`);
      const arrayBuffer = await response.arrayBuffer();
      
      await this.ensureInitialized();
      if (!this.context) throw new Error('Audio context not initialized');
      
      const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
      this.audioBufferCache.set(path, audioBuffer);
      return audioBuffer;
    }

    // Fall back to remote storage if not in cache
    const arrayBuffer = await this.fetchRemoteAudio(path);
    
    await this.ensureInitialized();
    if (!this.context) throw new Error('Audio context not initialized');
    
    const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
    this.audioBufferCache.set(path, audioBuffer);
    return audioBuffer;
  }

  private async playWithAudioElement(url: string): Promise<void> {
    if (this.isPlaying) {
      debugLog('[Audio] Skipping playWithAudioElement - already playing');
      return;
    }
  
    debugLog('[Audio] Creating audio element for URL:', url);
    const audio = new Audio(url);
    audio.setAttribute('playsinline', 'true');
    audio.setAttribute('webkit-playsinline', 'true');
    audio.setAttribute('x-webkit-airplay', 'allow');
    audio.volume = 1.0;
  
    this.setPreviewAudio(audio); // ✅ add this line
  
    this.isPlaying = true;
    try {
      debugLog('[Audio] Attempting to play audio element');
      await audio.play();
      debugLog('[Audio] Audio element playing successfully');
    } catch (error) {
      console.error('[Audio] Failed to play audio element:', error);
      this.isPlaying = false;
      throw error;
    }
  
    return new Promise(resolve => {
      audio.onended = () => {
        debugLog('[Audio] Audio element playback ended');
        this.isPlaying = false;
        resolve();
      };
    });
  }
  
  private async playWithWebAudio(url: string): Promise<void> {
    await this.ensureInitialized();
  
    if (!this.context || !this.gainNode) throw new Error('Audio context not ready');
  
    // Extra resume check (in case iOS auto-suspended it)
    if (this.context.state === 'suspended') {
      try {
        await this.context.resume();
        debugLog('[Audio] Resumed context before Web Audio playback');
      } catch (e) {
        console.warn('AudioContext resume failed:', e);
      }
    }
  
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
  
    const source = this.context.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.gainNode);
  
    this.isPlaying = true;
    source.start();
  
    return new Promise(resolve => {
      source.onended = () => {
        this.isPlaying = false;
        resolve();
      };
    });
  }
  

  async playTone(frequency: number, duration: number, type: OscillatorType = 'sine') {
    this.stopCurrentPlayback();
    
    await this.ensureInitialized();
    if (!this.context || !this.gainNode) return;
    
    const oscillator = this.context.createOscillator();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, this.context.currentTime);
    oscillator.connect(this.gainNode);
    this.currentOscillator = oscillator;
    this.isPlaying = true;
    oscillator.start();
    oscillator.stop(this.context.currentTime + duration);
    
    oscillator.onended = () => {
      if (this.currentOscillator === oscillator) {
        this.currentOscillator = null;
      }
      this.isPlaying = false;
    };

    return new Promise(resolve => {
      const checkEnd = () => {
        if (!this.currentOscillator || this.currentOscillator !== oscillator) {
          resolve(undefined);
        } else {
          setTimeout(checkEnd, 50);
        }
      };
      checkEnd();
    });
  }

  /**
   * Smooth chime: all notes scheduled on one context timeline with a soft
   * attack and exponential release (no clicks), notes overlapping slightly
   * so the chime rings instead of stepping. A quiet octave harmonic adds
   * warmth. The legacy beeps (playTone sequences) are preserved for the
   * 'legacy' sound style.
   */
  async playChime(notes: { frequency: number; start: number; duration: number }[]) {
    this.stopCurrentPlayback();

    await this.ensureInitialized();
    if (!this.context || !this.gainNode) return;

    const now = this.context.currentTime + 0.02;
    let totalEnd = now;

    for (const note of notes) {
      const start = now + note.start;
      const peak = 0.32;
      const attack = 0.015;

      const voice = (freq: number, gain: number) => {
        const oscillator = this.context!.createOscillator();
        const envelope = this.context!.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(freq, start);
        envelope.gain.setValueAtTime(0, start);
        envelope.gain.linearRampToValueAtTime(gain, start + 0.015);
        envelope.gain.exponentialRampToValueAtTime(0.0001, start + note.duration);
        oscillator.connect(envelope);
        envelope.connect(this.gainNode!);
        oscillator.start(start);
        oscillator.stop(start + note.duration + 0.05);
        this.currentChimeOscillators.push(oscillator);
      };

      voice(note.frequency, peak);
      voice(note.frequency * 2, peak * 0.15); // quiet octave for warmth
      totalEnd = Math.max(totalEnd, start + note.duration);
    }

    this.isPlaying = true;
    const totalMs = (totalEnd - now) * 1000;
    return new Promise(resolve => {
      setTimeout(() => {
        this.currentChimeOscillators = [];
        this.isPlaying = false;
        resolve(undefined);
      }, totalMs + 60);
    });
  }

  async playCustomSound(type: 'focus' | 'shortBreak' | 'longBreak', forcePlay = false) {
    this.stopCurrentPlayback();
    
    if (!this.hasUserInteraction && !forcePlay) {
      debugLog('[Audio] Skipping sound due to no user interaction');
      return;
    }

    const sound = this.customSounds[type];
    debugLog('[Audio] Custom sound for', type, ':', sound);

    if (!sound?.path) {
      debugLog('[Audio] No custom sound found, playing built-in', this.soundStyle, 'sound');
      if (this.soundStyle === 'legacy') {
        // Legacy beeps — the original abrupt sequential tones, preserved.
        if (type === 'focus') {
          await this.playTone(440, 0.2);
          await this.playTone(554.37, 0.2);
        } else if (type === 'shortBreak') {
          await this.playTone(523.25, 0.2);
          await this.playTone(392, 0.2);
        } else if (type === 'longBreak') {
          await this.playTone(659.25, 0.1);
          await this.playTone(783.99, 0.1);
          await this.playTone(987.77, 0.2);
        }
        return;
      }

      // Smooth chimes — enveloped, overlapping, key of C.
      if (type === 'focus') {
        await this.playChime([
          { frequency: 523.25, start: 0, duration: 0.6 },   // C5
          { frequency: 659.25, start: 0.15, duration: 0.8 }, // E5
        ]);
      } else if (type === 'shortBreak') {
        await this.playChime([
          { frequency: 392, start: 0, duration: 0.45 },      // G4
          { frequency: 523.25, start: 0.15, duration: 0.7 }, // C5
        ]);
      } else if (type === 'longBreak') {
        await this.playChime([
          { frequency: 523.25, start: 0, duration: 0.5 },    // C5
          { frequency: 659.25, start: 0.15, duration: 0.5 }, // E5
          { frequency: 783.99, start: 0.3, duration: 0.9 },  // G5
        ]);
      }
      return;
    }

    try {
      // Try to get from cache first
      debugLog('[Audio] Attempting to load from cache:', sound.path);
      const cachedUrl = await audioCache.loadFromCache(sound.path);
      debugLog('[Audio] Cache result:', cachedUrl ? 'Found' : 'Not found');

      if (cachedUrl) {
        debugLog('[Audio] Playing from cache URL');
        await this.playWithWebAudio(cachedUrl);
        return;
      }

      // Fall back to remote storage if not in cache
      debugLog('[Audio] Falling back to remote storage');
      const remoteBuffer = await this.fetchRemoteAudio(sound.path);
      const blobUrl = URL.createObjectURL(new Blob([remoteBuffer]));
      
      try {
        debugLog('[Audio] Playing with audio element');
        await this.playWithAudioElement(blobUrl);
        URL.revokeObjectURL(blobUrl);
        return;
      } catch (audioElementError) {
        URL.revokeObjectURL(blobUrl);
        console.warn('[Audio] Audio element playback failed, falling back to Web Audio:', audioElementError);
      }

      await this.ensureInitialized();
      if (!this.context || !this.gainNode) throw new Error('Audio context not ready');
      
      debugLog('[Audio] Playing with Web Audio API');
      const buffer = await this.fetchAndDecodeAudio(sound.path);
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.connect(this.gainNode);
      
      this.currentSourceNode = source;
      this.isPlaying = true;
      source.start();
      return new Promise(resolve => {
        source.onended = () => {
          if (this.currentSourceNode === source) {
            this.currentSourceNode = null;
          }
          this.isPlaying = false;
          resolve(undefined);
        };
      });
    } catch (error) {
      console.error('[Audio] Custom sound failed:', error);
      this.isPlaying = false;
    }
  }

  async playFocusSound(forcePlay = false) {
    this.hasUserInteraction = true;
    await this.playCustomSound('focus', forcePlay);
  }

  async playBreakSound(forcePlay = false) {
    this.hasUserInteraction = true;
    await this.playCustomSound('shortBreak', forcePlay);
  }

  async playLongBreakSound(forcePlay = false) {
    this.hasUserInteraction = true;
    await this.playCustomSound('longBreak', forcePlay);
  }
  
  clearCache() {
    this.audioBufferCache.clear();
    this.audioElements.clear();
  }

  stopAllSounds() {
    this.isPlaying = false;
    
    // Stop any playing audio elements
    this.audioElements.forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
    this.audioElements.clear();

    // Stop any playing preview
    this.stopPreview();

    // Stop any playing source
    if (this.previewSource) {
      try {
        this.previewSource.stop();
      } catch (error) {
        console.warn('[Audio] Stop preview failed:', error);
      }
      this.previewSource = null;
    }

    // Don't close the context, just stop the sounds
    if (this.context?.state === 'running') {
      this.context.suspend();
    }
  }

  async playPreview(url: string, onEnded?: () => void) {
    if (this.previewSource) {
      this.stopPreview();
    }

    try {
      await this.ensureInitialized();
      if (!this.context || !this.gainNode) throw new Error('Audio context not ready');

      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
      
      this.previewSource = this.context.createBufferSource();
      this.previewSource.buffer = audioBuffer;
      this.previewSource.connect(this.gainNode);
      
      if (onEnded) {
        this.previewSource.onended = onEnded;
      }
      
      this.previewSource.start();
    } catch (error) {
      console.error('[Audio] Preview failed:', error);
      if (onEnded) onEnded();
    }
  }

  stopPreview() {
    if (this.previewAudio) {
      try {
        this.previewAudio.pause();
        this.previewAudio.currentTime = 0;
      } catch (error) {
        console.warn('[Audio] Stop preview failed:', error);
      }
      this.previewAudio = null;
    }
  }

  setCustomSound(type: 'focus' | 'shortBreak' | 'longBreak', path: string) {
    debugLog('[Audio] Setting custom sound for', type, ':', path);
    if (path) {
      this.customSounds[type] = { path };
    } else {
      delete this.customSounds[type];
    }
    this.audioBufferCache.delete(path);
  }

  setPreviewAudio(audio: HTMLAudioElement) {
    // Stop any existing preview
    this.stopPreview();
    this.previewAudio = audio;
  }

  // Make public so it can be called externally (e.g., on timer pause)
  public stopCurrentPlayback(): void {
    // Stop HTML Audio Element preview/playback
    if (this.previewAudio) {
      try {
        this.previewAudio.pause();
        this.previewAudio.currentTime = 0;
        debugLog('[Audio] Stopped preview audio element.');
      } catch (e) { console.warn("[Audio] Error stopping preview audio element:", e); }
      this.previewAudio = null;
    }

    // Stop Web Audio API BufferSource preview
    if (this.previewSource) {
      try {
        this.previewSource.stop();
        debugLog('[Audio] Stopped preview source node.');
      } catch (e) { /* Ignore errors if already stopped */ }
      this.previewSource.onended = null; // Prevent lingering onended call
      this.previewSource = null;
    }

    // Stop Web Audio API BufferSource playback
    if (this.currentSourceNode) {
      try {
        this.currentSourceNode.stop();
        debugLog('[Audio] Stopped current source node.');
      } catch (e) { /* Ignore errors if already stopped */ }
      this.currentSourceNode.onended = null; // Prevent lingering onended call
      this.currentSourceNode = null;
    }

    // Stop Web Audio API Oscillator playback
    if (this.currentOscillator) {
      try {
        this.currentOscillator.stop();
        debugLog('[Audio] Stopped current oscillator.');
      } catch (e) { /* Ignore errors if already stopped */ }
      this.currentOscillator.onended = null; // Prevent lingering onended call
      this.currentOscillator = null;
    }

    // Stop any scheduled chime oscillators
    for (const osc of this.currentChimeOscillators) {
      try { osc.stop(); } catch (e) { /* already stopped */ }
    }
    this.currentChimeOscillators = [];

    // Reset the flag
    if (this.isPlaying) {
        // debugLog('[Audio] Resetting isPlaying flag in stopCurrentPlayback.');
        this.isPlaying = false;
    }
  }
}

export const audioManager = new AudioManager();
